import React from 'react';
import { useSelector } from 'react-redux';
import { useTheme } from '@mui/material/styles';
import { getThemePaths } from './themePaths';

/**
 * Ordem de prioridade (dark/light):
 *   1) Público da revenda:
 *      - dark:   /themes/:code/logoInverted.svg
 *      - light:  /themes/:code/logo.svg
 *   2) API da revenda (sem HEAD, direto GET)
 *      - /api/revendas/logo/:code
 *   3) Público raiz por tema:
 *      - dark:   /logoInverted.svg
 *      - light:  /logo.svg
 *   4) Fallback físico global:
 *      - /resources/images/logo.svg
 */

const HEAD_OK_CACHE = new Map();

async function headOk(url) {
  try {
    if (HEAD_OK_CACHE.has(url)) return HEAD_OK_CACHE.get(url);
    const res = await fetch(url, { method: 'HEAD', cache: 'no-store' });
    const ok = res.ok;
    HEAD_OK_CACHE.set(url, ok);
    return ok;
  } catch {
    HEAD_OK_CACHE.set(url, false);
    return false;
  }
}

const LogoImage = ({
 className, style, alt = 'Logo', height, width, forceLightTheme = false, forceDarkTheme = false, onLoad, onError, ...rest
}) => {
  const theme = useTheme();
  const isDark = forceDarkTheme ? true : (forceLightTheme ? false : theme.palette.mode === 'dark');
  const revenda = useSelector((state) => state.session?.revenda);
  const loadCallbackCalled = React.useRef(false);

  const candidates = React.useMemo(() => {
    const paths = getThemePaths(revenda);
    const code = revenda?.code || null;
    const list = [];

    // 1) Público da revenda
    if (code) {
      list.push(isDark ? paths.logoDark : paths.logoLight);
    }

    // 2) API da revenda
    if (code) {
      const themeParam = isDark ? 'dark' : 'light';
      list.push(`/api/revendas/logo/${code}?theme=${themeParam}`);
    }

    // 3) Público raiz
    list.push(isDark ? '/logoInverted.svg' : '/logo.svg');

    // 4) Fallback físico
    list.push('/resources/images/logo.svg');

    return Array.from(new Set(list));
  }, [revenda?.code, isDark]);

  const headable = React.useMemo(
    () => candidates.map((url) => !url.startsWith('/api/revendas/logo/')),
    [candidates]
  );

  const [index, setIndex] = React.useState(0);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      for (let i = 0; i < candidates.length; i++) {
        const url = candidates[i];
        if (headable[i]) {
          const ok = await headOk(url);
          if (cancelled) return;
          if (ok) {
            setIndex(i);
            return;
          }
        } else {
          setIndex(i);
          return;
        }
      }
      if (!cancelled) setIndex(candidates.length - 1);
    })();
    return () => { cancelled = true; };
  }, [candidates, headable]);

  const handleError = React.useCallback((e) => {
    const isLastCandidate = index >= candidates.length - 1;
    if (!isLastCandidate) {
      setIndex((i) => i + 1);
    } else if (onError && !loadCallbackCalled.current) {
      // Last candidate failed - call parent's onError
      loadCallbackCalled.current = true;
      onError(e);
    }
  }, [candidates.length, index, onError]);

  const handleLoad = React.useCallback((e) => {
    if (onLoad && !loadCallbackCalled.current) {
      loadCallbackCalled.current = true;
      onLoad(e);
    }
  }, [onLoad]);

  const finalStyle = {
    display: 'block',
    height: height ?? 56,
    width: width ?? 'auto',
    objectFit: 'contain',
    margin: '0 auto',
    ...style,
  };

  return (
    <img
      src={candidates[index]}
      alt={alt}
      className={className}
      style={finalStyle}
      onError={handleError}
      onLoad={handleLoad}
      {...rest}
    />
  );
}

export { LogoImage };
export default LogoImage;
